<template>
  <div v-if="pageVisible" class="email-login-modal">
    <div class="email-login-modal__mask">
      <div class="email-login-modal__card" :class="`view-${currentView}`">
        <button
          class="email-login-modal__close"
          type="button"
          :aria-label="copy.close"
          @click="closePage"
        >
          <img src="@/assets/icons/closeBtn.svg" alt="" />
        </button>

        <div class="email-login-modal__header">
          <img
            v-if="currentView !== 'sent'"
            class="email-login-modal__logo"
            src="@/assets/img/login/logo-mureka.svg"
            alt="Mureka"
          />
          <img
            v-else
            class="email-login-modal__sent-icon"
            src="@/assets/img/login/email-sent.png"
            alt=""
          />
          <h2>{{ copy.title }}</h2>
          <p v-if="copy.subtitle">{{ copy.subtitle }}</p>
        </div>

        <template v-if="currentView === 'entry'">
          <GoogleLogin :callback="googleSigninCallback" popup-type="TOKEN">
            <button class="email-login-modal__google" type="button">
              <img src="@/assets/icons/google.svg" alt="" />
              <span>Continue with Google</span>
            </button>
          </GoogleLogin>

          <div class="email-login-modal__divider">
            <span class="divider-first"></span>
            <em>or</em>
            <span class="divider-last"></span>
          </div>

          <div class="email-login-modal__socials">
            <button type="button" aria-label="Facebook" @click="facebookLogin">
              <img src="@/assets/icons/login-social-facebook.svg" alt="" />
            </button>
            <button type="button" aria-label="Apple" @click="appleLogin">
              <img src="@/assets/icons/login-social-apple.svg" alt="" />
            </button>
            <button type="button" aria-label="Discord" @click="discordLogin">
              <img src="@/assets/icons/login-social-discord.svg" alt="" />
            </button>
            <button type="button" aria-label="Email" @click="openEmailSignIn">
              <img src="@/assets/icons/login-social-email.svg" alt="" />
            </button>
          </div>
        </template>

        <template v-else-if="currentView === 'signin'">
          <div v-if="region === 'cn'" class="email-login-modal__tabs">
            <button
              type="button"
              :class="{ active: loginMethod === 'phone' }"
              @click="switchLoginMethod('phone')"
            >
              手机登录
            </button>
            <button
              type="button"
              :class="{ active: loginMethod === 'email' }"
              @click="switchLoginMethod('email')"
            >
              邮箱登录
            </button>
          </div>

          <form
            class="email-login-modal__form"
            novalidate
            @submit.prevent="submitSignIn"
          >
            <label
              class="email-login-modal__field"
              :class="{ error: Boolean(errors.account) }"
            >
              <input
                v-model.trim="form.account"
                :aria-invalid="Boolean(errors.account)"
                :inputmode="loginMethod === 'phone' ? 'tel' : 'email'"
                :maxlength="loginMethod === 'phone' ? 11 : 80"
                :placeholder="accountPlaceholder"
                :type="loginMethod === 'phone' ? 'tel' : 'text'"
                :autocomplete="loginMethod === 'phone' ? 'tel' : 'email'"
                @input="handleAccountInput"
              />
            </label>

            <label
              class="email-login-modal__field password"
              :class="{ error: Boolean(errors.password) }"
            >
              <input
                v-model="form.password"
                :aria-invalid="Boolean(errors.password)"
                :maxlength="loginMethod === 'phone' ? 6 : 64"
                :placeholder="passwordPlaceholder"
                :type="passwordVisible ? 'text' : 'password'"
                :autocomplete="
                  loginMethod === 'phone' ? 'one-time-code' : 'current-password'
                "
                @input="handlePasswordInput"
              />
              <button
                v-if="loginMethod === 'email'"
                class="email-login-modal__eye"
                :class="{ visible: passwordVisible }"
                type="button"
                :aria-label="
                  passwordVisible ? copy.hidePassword : copy.showPassword
                "
                @click="passwordVisible = !passwordVisible"
              ></button>
              <button
                v-if="loginMethod === 'phone'"
                class="email-login-modal__code"
                type="button"
                :disabled="codeSending"
                @click="sendPhoneCode"
              >
                {{ codeButtonText }}
              </button>
            </label>

            <div class="email-login-modal__form-meta">
              <p v-if="fieldErrorText" class="email-login-modal__form-error">
                {{ fieldErrorText }}
              </p>
              <button
                v-if="loginMethod === 'email'"
                class="email-login-modal__forgot"
                type="button"
                @click="openForgotPassword"
              >
                {{ copy.forgotPassword }}
              </button>
            </div>

            <div class="email-login-modal__actions">
              <button
                class="email-login-modal__primary"
                type="submit"
                :disabled="submitDisabled || submitting"
              >
                {{ copy.signIn }}
              </button>
              <p class="email-login-modal__switch-copy">
                {{ copy.noAccount }}
                <button type="button" @click="openSignUp">
                  {{ copy.signUp }}
                </button>
              </p>
            </div>
          </form>
        </template>

        <template v-else-if="currentView === 'signup'">
          <form
            class="email-login-modal__form"
            novalidate
            @submit.prevent="submitSignUp"
          >
            <label
              class="email-login-modal__field"
              :class="{ error: Boolean(errors.account) }"
            >
              <input
                v-model.trim="form.account"
                :aria-invalid="Boolean(errors.account)"
                inputmode="email"
                maxlength="80"
                :placeholder="copy.emailPlaceholder"
                type="text"
                autocomplete="email"
                @input="handleAccountInput"
              />
            </label>
            <label
              class="email-login-modal__field password"
              :class="{ error: Boolean(errors.password) }"
            >
              <input
                v-model="form.password"
                :aria-invalid="Boolean(errors.password)"
                maxlength="64"
                :placeholder="copy.passwordPlaceholder"
                :type="passwordVisible ? 'text' : 'password'"
                autocomplete="new-password"
                @input="handlePasswordInput"
              />
              <button
                class="email-login-modal__eye"
                :class="{ visible: passwordVisible }"
                type="button"
                :aria-label="
                  passwordVisible ? copy.hidePassword : copy.showPassword
                "
                @click="passwordVisible = !passwordVisible"
              ></button>
            </label>

            <p v-if="fieldErrorText" class="email-login-modal__signup-error">
              {{ fieldErrorText }}
            </p>
            <ul class="email-login-modal__rules">
              <li
                v-for="rule in passwordRuleResults"
                :key="rule.label"
                :class="{ valid: rule.passed }"
              >
                <img
                  :src="rule.passed ? passwordRuleValid : passwordRuleInvalid"
                  alt=""
                  aria-hidden="true"
                />
                {{ rule.label }}
              </li>
            </ul>

            <div class="email-login-modal__actions signup-actions">
              <button
                class="email-login-modal__primary"
                type="submit"
                :disabled="signUpDisabled || submitting"
              >
                {{ copy.signUp }}
              </button>
              <p class="email-login-modal__switch-copy">
                {{ copy.haveAccount }}
                <button type="button" @click="returnToSignIn">
                  {{ copy.signIn }}
                </button>
              </p>
            </div>
          </form>
        </template>

        <template v-else-if="currentView === 'forgot'">
          <form
            class="email-login-modal__form compact"
            novalidate
            @submit.prevent="submitForgot"
          >
            <div class="email-login-modal__forgot-field-group">
              <label
                class="email-login-modal__field"
                :class="{ error: Boolean(errors.account) }"
              >
                <input
                  v-model.trim="form.account"
                  :aria-invalid="Boolean(errors.account)"
                  :aria-describedby="
                    fieldErrorText ? 'forgot-email-error' : undefined
                  "
                  inputmode="email"
                  maxlength="80"
                  :placeholder="copy.emailPlaceholder"
                  type="text"
                  autocomplete="email"
                  @input="handleAccountInput"
                />
              </label>
              <p
                v-if="fieldErrorText"
                id="forgot-email-error"
                class="email-login-modal__inline-error"
                role="alert"
              >
                {{ fieldErrorText }}
              </p>
            </div>
            <div class="email-login-modal__actions forgot-actions">
              <button
                class="email-login-modal__primary"
                type="submit"
                :disabled="!form.account || submitting"
              >
                {{ copy.send }}
              </button>
              <button
                class="email-login-modal__text-button"
                type="button"
                @click="returnToSignIn"
              >
                {{ copy.returnToSignIn }}
              </button>
            </div>
          </form>
        </template>

        <template v-else>
          <div class="email-login-modal__sent">
            <p>{{ sentDescription }}</p>
            <p>{{ copy.sentTip }}</p>
            <button
              class="email-login-modal__primary"
              type="button"
              :disabled="resendCountdown > 0 || submitting"
              @click="resendEmail"
            >
              {{ resendText }}
            </button>
            <button
              class="email-login-modal__text-button"
              type="button"
              @click="returnToSignIn"
            >
              {{ copy.returnToSignIn }}
            </button>
          </div>
        </template>

        <div
          v-if="showTerms"
          class="email-login-modal__terms"
          :class="{ cn: region === 'cn' }"
        >
          <template v-if="region === 'cn' && currentView !== 'sent'">
            <MuCheckboxGroup
              v-model="termsChannels"
              direction="vertical"
              name="login-cn-terms"
            >
              <MuCheckbox value="terms">
                <span class="email-login-modal__terms-text">
                  请阅读并同意<span
                    class="a-link btn pointer"
                    @click.stop.prevent="openPolicy('terms')"
                    >《Mureka用户协议》</span
                  >
                  <span
                    class="a-link btn pointer"
                    @click.stop.prevent="openPolicy('privacy')"
                    >《Mureka隐私政策》</span
                  >
                </span>
              </MuCheckbox>
            </MuCheckboxGroup>
          </template>
          <template v-else-if="region === 'overseas'">
            By continuing, you agree to
            <a @click="openPolicy('terms')">Terms of Use</a> and
            <a @click="openPolicy('privacy')">Privacy Policy</a>
          </template>
          <template v-else>
            继续即表示您同意<a @click="openPolicy('terms')">使用条款</a>和<a
              @click="openPolicy('privacy')"
              >隐私政策</a
            >
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable vue/prop-name-casing */
import { MuCheckbox, MuCheckboxGroup } from "@mureka-fe/ui";
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";

import apiUser from "@/api/api/apiUser.js";
import passwordRuleInvalid from "@/assets/icons/password-rule-invalid.svg";
import passwordRuleValid from "@/assets/icons/password-rule-valid.svg";
import message from "@/components/functionCallComponent/message/message.js";
import globleConfig from "@/config/config.js";
import { firebaseUtils } from "@/utils/analytic/firebaseUtils.js";

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  region: {
    type: String,
    default: "overseas",
  },
  handleOk: {
    type: Function,
    default: null,
  },
  handleCancel: {
    type: Function,
    default: null,
  },
  remove: {
    type: Function,
    default: null,
  },
  rightType: {
    type: String,
    default: "login",
  },
  create_type: {
    type: String,
    default: "2",
  },
  source: {
    type: String,
    default: "other",
  },
  signup_source: {
    type: String,
    default: "",
  },
  initialEmail: {
    type: String,
    default: "",
  },
  submitEmailPassword: {
    type: Function,
    default: null,
  },
  submitPhoneCode: {
    type: Function,
    default: null,
  },
  sendPhoneVerifyCode: {
    type: Function,
    default: null,
  },
  continueWithGoogle: {
    type: Function,
    default: null,
  },
  facebookLogin: {
    type: Function,
    default: null,
  },
  discordLogin: {
    type: Function,
    default: null,
  },
  appleLogin: {
    type: Function,
    default: null,
  },
});

const copyMap = {
  overseas: {
    title: "Sign in to Mureka",
    subtitle: "",
    signUpTitle: "Sign up to Mureka",
    emailPlaceholder: "Email address",
    passwordPlaceholder: "Password",
    signIn: "Sign in",
    signUp: "Sign up",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    forgotPassword: "Forgot password?",
    forgotTitle: "Forgot password?",
    forgotSubtitle: "Enter the email address for password reset",
    send: "Send",
    sentTitle: "Check your email",
    sentReset:
      "Thanks! If {email} matches an email address we have on file, then we've sent you an email containing further instructions for resetting your password.",
    sentVerify:
      "We've sent an email to {email}. Click the link to verify your address and complete your sign up.",
    sentTip:
      "If you haven't received an email in 5 minutes, check your spam, resend, or try a different email address.",
    returnToSignIn: "Return to Sign In",
    invalidEmail: "Invalid email",
    invalidPassword: "Incorrect password",
    noAccountFound: "No account found. Please sign up.",
    accountExists: "Email already registered. Please sign in.",
    accountAlreadyVerified:
      "The account already exists. Please input email address and password to log in.",
    emailRequired: "Please enter your email address",
    passwordRequired: "Please enter your password",
    passwordRules: [
      "Minimum 8 characters",
      "At least one number",
      "At least one letter(a-z, A-Z)",
    ],
    agreeRequired:
      "Please read and agree to the Terms of Use and Privacy Policy.",
    sendFailed: "Failed to send email. Please try again later.",
    resend: "Resend",
    close: "Close",
    showPassword: "Show password",
    hidePassword: "Hide password",
  },
  cn: {
    title: "登录Mureka",
    subtitle: "",
    signUpTitle: "注册Mureka",
    emailPlaceholder: "请输入邮箱",
    phonePlaceholder: "请输入手机号",
    passwordPlaceholder: "请输入密码",
    codePlaceholder: "请输入验证码",
    signIn: "登录",
    signUp: "注册",
    noAccount: "还没有账号？",
    haveAccount: "已有账号？",
    forgotPassword: "忘记密码？",
    forgotTitle: "忘记密码",
    forgotSubtitle: "请输入用于密码重置的电子邮箱地址",
    send: "发送",
    sentTitle: "检查您的邮箱",
    sentReset:
      "如果 {email} 与我们记录中的邮箱地址匹配，我们已向您发送包含重置密码说明的邮件。",
    sentVerify:
      "我们已向 {email} 发送了验证邮件。请点击邮件中的链接验证邮箱地址并完成注册。",
    sentTip:
      "如果 5 分钟内没有收到邮件，请检查垃圾邮件、重新发送，或尝试其他邮箱地址。",
    returnToSignIn: "返回登录",
    invalidEmail: "邮箱格式错误",
    invalidPassword: "密码错误",
    noAccountFound: "未找到账号，请注册。",
    accountExists: "该邮箱已注册，请登录。",
    accountAlreadyVerified: "该账号已存在，请输入邮箱地址和密码登录。",
    invalidPhone: "手机号格式不正确",
    invalidCode: "验证码不足6位",
    emailRequired: "请输入邮箱",
    phoneRequired: "请输入手机号",
    passwordRequired: "请输入密码",
    codeRequired: "请输入验证码",
    passwordRules: ["至少8个字符", "至少包含1个数字", "至少1个字母（a-z，A-Z)"],
    agreeRequired: "请阅读并同意用户协议与隐私政策。",
    sendFailed: "邮件发送失败，请稍后重试。",
    resend: "重新发送",
    close: "关闭",
    showPassword: "显示密码",
    hidePassword: "隐藏密码",
  },
};

const emailReg =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}\])|(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}))$/;
const phoneReg = /^1[3-9]\d{9}$/;
const passwordRuleChecks = [
  (value) => value.length >= 8,
  (value) => /\d/.test(value),
  (value) => /[A-Za-z]/.test(value),
];

const pageVisible = ref(props.visible);
const currentView = ref(
  props.region === "overseas" && !props.initialEmail ? "entry" : "signin"
);
const loginMethod = ref(
  props.region === "cn" && !props.initialEmail ? "phone" : "email"
);
const passwordVisible = ref(false);
const submitting = ref(false);
const codeSending = ref(false);
const codeCountdown = ref(60);
const resendCountdown = ref(0);
const termsChannels = ref([]);
const sentType = ref("reset");
const passwordTouchedBySubmit = ref(false);
let codeTimer = null;
let resendTimer = null;

const form = reactive({
  account: props.initialEmail,
  password: "",
});
const errors = reactive({
  account: "",
  password: "",
});

const copy = computed(() => {
  const base = copyMap[props.region] || copyMap.overseas;
  if (currentView.value === "signup") {
    return { ...base, title: base.signUpTitle, subtitle: "" };
  }
  if (currentView.value === "forgot") {
    return {
      ...base,
      title: base.forgotTitle,
      subtitle: base.forgotSubtitle,
    };
  }
  if (currentView.value === "sent") {
    return {
      ...base,
      title: base.sentTitle,
      subtitle: "",
    };
  }
  return base;
});

const region = computed(() => props.region);
const termCheckbox = computed(() => termsChannels.value.includes("terms"));
const showTerms = computed(() => currentView.value !== "forgot");

const accountPlaceholder = computed(() => {
  if (loginMethod.value === "phone") {
    return copy.value.phonePlaceholder;
  }
  return copy.value.emailPlaceholder;
});

const passwordPlaceholder = computed(() => {
  if (loginMethod.value === "phone") {
    return copy.value.codePlaceholder;
  }
  return copy.value.passwordPlaceholder;
});

const submitDisabled = computed(() => {
  return !form.account || !form.password;
});

const passwordRuleResults = computed(() =>
  copy.value.passwordRules.map((label, index) => ({
    label,
    passed: passwordRuleChecks[index](form.password),
  }))
);

const signUpDisabled = computed(
  () => !form.account || !passwordRuleResults.value.every((rule) => rule.passed)
);

const fieldErrorText = computed(() => errors.account || errors.password);

const codeButtonText = computed(() => {
  if (codeSending.value) {
    return `${codeCountdown.value}s`;
  }
  return "获取验证码";
});

const sentDescription = computed(() => {
  const key = sentType.value === "verify" ? "sentVerify" : "sentReset";
  return copy.value[key].replace(
    "{email}",
    form.account || copy.value.emailPlaceholder
  );
});

const resendText = computed(() => {
  if (resendCountdown.value > 0) {
    return `${copy.value.resend}(${resendCountdown.value}s)`;
  }
  return copy.value.resend;
});

const track = (event, extra = {}) => {
  firebaseUtils.logClickEvent(event, {
    create_type: props.create_type,
    source: "web",
    entry_source: props.source,
    page_route: window.location.pathname,
    ...extra,
  });
};

watch(
  () => pageVisible.value,
  (val) => {
    if (!val && typeof props.remove === "function") {
      props.remove();
    }
  }
);

watch(
  () => props.visible,
  (val) => {
    pageVisible.value = val;
  }
);

const resetErrors = () => {
  errors.account = "";
  errors.password = "";
};

const clearFieldError = (key) => {
  errors[key] = "";
};

const handleAccountInput = () => {
  clearFieldError("account");
};

const handlePasswordInput = () => {
  clearFieldError("password");
};

const showAgreeError = () => {
  message.error({
    maskClosable: true,
    position: "top",
    content: copy.value.agreeRequired,
  });
};

const switchLoginMethod = (method) => {
  if (loginMethod.value === method) return;
  loginMethod.value = method;
  form.account = "";
  form.password = "";
  passwordVisible.value = false;
  passwordTouchedBySubmit.value = false;
  resetErrors();
};

const openEmailSignIn = () => {
  loginMethod.value = "email";
  currentView.value = "signin";
  track("login_page_email_click");
};

const openSignUp = () => {
  const validEmail = emailReg.test(form.account);
  form.account = validEmail ? form.account : "";
  form.password = "";
  loginMethod.value = "email";
  passwordVisible.value = false;
  passwordTouchedBySubmit.value = false;
  resetErrors();
  currentView.value = "signup";
  track("sign_up_page_show");
};

const closePage = () => {
  pageVisible.value = false;
  localStorage.removeItem(`${globleConfig.appname}_create_data`);
  firebaseUtils.logClickEvent(
    props.rightType === "generate" ? "close_new_user_login" : "close_login",
    {
      create_type: props.create_type,
      page_route: window.location.pathname,
      source: props.source,
    }
  );
  if (typeof props.handleCancel === "function") {
    props.handleCancel(false);
  }
};

const validateEmail = ({ allowEmpty = false } = {}) => {
  if (!form.account) {
    if (allowEmpty) {
      errors.account = "";
      return true;
    }
    errors.account = copy.value.emailRequired;
    return false;
  }
  if (!emailReg.test(form.account)) {
    errors.account = copy.value.invalidEmail;
    return false;
  }
  errors.account = "";
  return true;
};

const validatePhone = () => {
  if (!form.account) {
    errors.account = copy.value.phoneRequired;
    return false;
  }
  if (!phoneReg.test(form.account)) {
    errors.account = copy.value.invalidPhone;
    return false;
  }
  return true;
};

const validatePassword = () => {
  passwordTouchedBySubmit.value = true;
  if (!form.password) {
    errors.password =
      loginMethod.value === "phone"
        ? copy.value.codeRequired
        : copy.value.passwordRequired;
    return false;
  }
  if (loginMethod.value === "phone" && form.password.length < 6) {
    errors.password = copy.value.invalidCode;
    return false;
  }
  return true;
};

const validateSignUpPassword = () => {
  passwordTouchedBySubmit.value = true;
  const failedIndex = passwordRuleChecks.findIndex(
    (check) => !check(form.password)
  );
  if (failedIndex >= 0) {
    errors.password = copy.value.passwordRules[failedIndex];
    return false;
  }
  errors.password = "";
  return true;
};

const startCodeCountdown = () => {
  clearInterval(codeTimer);
  codeSending.value = true;
  codeCountdown.value = 60;
  codeTimer = window.setInterval(() => {
    codeCountdown.value -= 1;
    if (codeCountdown.value <= 0) {
      codeSending.value = false;
      codeCountdown.value = 60;
      clearInterval(codeTimer);
    }
  }, 1000);
};

const startResendCountdown = () => {
  clearInterval(resendTimer);
  resendCountdown.value = 58;
  resendTimer = window.setInterval(() => {
    resendCountdown.value -= 1;
    if (resendCountdown.value <= 0) {
      clearInterval(resendTimer);
    }
  }, 1000);
};

const sendPhoneCode = async () => {
  resetErrors();
  if (!validatePhone() || codeSending.value) return;
  if (typeof props.sendPhoneVerifyCode !== "function") return;

  submitting.value = true;
  try {
    const ok = await props.sendPhoneVerifyCode(form.account);
    if (ok !== false) {
      startCodeCountdown();
    }
  } finally {
    submitting.value = false;
  }
};

const submitSignIn = async () => {
  resetErrors();
  const validAccount =
    loginMethod.value === "phone" ? validatePhone() : validateEmail();
  const validPassword = validatePassword();
  if (!validAccount || !validPassword || submitting.value) return;

  if (region.value === "cn" && !termCheckbox.value) {
    showAgreeError();
    return;
  }

  track(
    loginMethod.value === "email"
      ? "sign_in_email_submit_click"
      : props.rightType === "generate"
        ? "newuser_login"
        : "login",
    { loginIsAgree: termCheckbox.value ? "1" : "0" }
  );

  submitting.value = true;
  try {
    const submitter =
      loginMethod.value === "phone"
        ? props.submitPhoneCode
        : props.submitEmailPassword;
    if (typeof submitter !== "function") return;
    const result = await submitter({
      account: form.account,
      password: form.password,
      method: loginMethod.value,
      intent: "signin",
    });

    if (result?.fieldErrors) {
      Object.assign(errors, result.fieldErrors);
      return;
    }

    if (result !== false) {
      if (loginMethod.value === "email") {
        track("sign_in_email_submit_success");
      }
      pageVisible.value = false;
    }
  } finally {
    submitting.value = false;
  }
};

const submitSignUp = async () => {
  resetErrors();
  const validEmail = validateEmail();
  const validPassword = validateSignUpPassword();
  if (!validEmail || !validPassword || submitting.value) return;

  if (region.value === "cn" && !termCheckbox.value) {
    showAgreeError();
    return;
  }

  track("sign_up_submit_click");
  submitting.value = true;
  try {
    if (typeof props.submitEmailPassword !== "function") return;
    const result = await props.submitEmailPassword({
      account: form.account,
      password: form.password,
      method: "email",
      intent: "signup",
    });

    if (result?.blocked) {
      track("register_blocked", {
        is_new_user: result.isNewUser ? 1 : 0,
      });
    }
    if (result?.fieldErrors) {
      Object.assign(errors, result.fieldErrors);
      return;
    }
    if (!result?.sentVerification) return;

    track("sign_up_submit_success");
    track("sign_up_check_email_show");
    sentType.value = "verify";
    currentView.value = "sent";
    startResendCountdown();
  } finally {
    submitting.value = false;
  }
};

const openForgotPassword = () => {
  currentView.value = "forgot";
  form.password = "";
  passwordTouchedBySubmit.value = false;
  resetErrors();
  track("forget_password_click");
  track("forget_password_email_show");
};

const submitForgot = async () => {
  resetErrors();
  if (!validateEmail() || submitting.value) return;

  track("forget_password_submit_click");

  submitting.value = true;
  try {
    const res = await apiUser.requestEmailPasswordReset({
      email: form.account,
      meta: { source: "web" },
    });
    if (res?.code !== 200) {
      errors.account =
        res?.code === 9008 || res?.code === 9017
          ? copy.value.noAccountFound
          : copy.value.sendFailed;
      return;
    }
    sentType.value = "reset";
    currentView.value = "sent";
    startResendCountdown();
    track("forget_password_check_email_click");
  } finally {
    submitting.value = false;
  }
};

const resendEmail = async () => {
  if (resendCountdown.value > 0 || submitting.value) return;
  submitting.value = true;
  try {
    if (sentType.value === "reset") {
      const res = await apiUser.requestEmailPasswordReset({
        email: form.account,
        meta: { source: "web" },
      });
      if (res?.code !== 200) {
        if (res?.code === 9008) {
          currentView.value = "forgot";
          resetErrors();
          errors.account = copy.value.noAccountFound;
          return;
        }
        message.error({
          maskClosable: true,
          position: "top",
          content:
            res?.code === 9017
              ? copy.value.noAccountFound
              : copy.value.sendFailed,
        });
        return;
      }
      track("forget_password_resend_click");
      startResendCountdown();
      return;
    }

    const registrationErrors = {
      9016: copy.value.accountExists,
      9021: copy.value.accountAlreadyVerified,
    };
    const res = await apiUser.requestEmailVerification({
      email: form.account,
      source: "web",
    });
    if (res?.code !== 200) {
      message.error({
        maskClosable: true,
        position: "top",
        content: registrationErrors[res?.code] || copy.value.sendFailed,
      });
      return;
    }
    track("sign_up_resend_click");
    startResendCountdown();
  } finally {
    submitting.value = false;
  }
};

const returnToSignIn = () => {
  if (currentView.value === "signup") {
    track("sign_up_page_sign_in_click");
  } else if (currentView.value === "sent" && sentType.value === "verify") {
    track("sign_up_check_email_sign_click");
  }
  currentView.value = "signin";
  loginMethod.value = "email";
  form.password = "";
  passwordVisible.value = false;
  resetErrors();
};

const googleSigninCallback = async (authResponse) => {
  if (typeof props.continueWithGoogle !== "function") return;
  await props.continueWithGoogle({
    x_auth_mode: "google_login",
    x_auth_token: authResponse.access_token,
  });
};

const facebookLogin = () => {
  if (typeof props.facebookLogin === "function") {
    props.facebookLogin();
  }
};

const discordLogin = () => {
  if (typeof props.discordLogin === "function") {
    props.discordLogin();
  }
};

const appleLogin = () => {
  if (typeof props.appleLogin === "function") {
    props.appleLogin();
  }
};

const openPolicy = (type) => {
  const isCn = globleConfig.appname === "MurekaCn";
  const path =
    type === "terms"
      ? isCn
        ? "/service_terms_cn.pdf"
        : "/service_terms.pdf"
      : isCn
        ? "/privacy_policy_cn.pdf"
        : "/privacy_policy.pdf";
  window.open(import.meta.env.VITE_PLATFORM_API_URL + path, "_blank");
};

onMounted(() => {
  pageVisible.value = props.visible;
  track("login_page_show");
});

onBeforeUnmount(() => {
  clearInterval(codeTimer);
  clearInterval(resendTimer);
});

defineExpose({ remove: props.remove });
</script>

<style lang="scss" scoped>
.email-login-modal {
  position: fixed;
  inset: 0;
  z-index: 3000;
  color: #000;
  background: rgba(0, 0, 0, 0.55);

  &__mask {
    display: flex;
    width: 100%;
    height: 100%;
    align-items: center;
    justify-content: center;
    padding: 24px;
    box-sizing: border-box;
  }

  &__card {
    position: relative;
    width: 460px;
    max-width: min(460px, calc(100vw - 64px));
    padding: 24px;
    border: 1px solid #fff;
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 32px 40px rgba(0, 0, 0, 0.4);
    box-sizing: border-box;

    &.view-sent {
      padding-top: 24px;
    }
  }

  .g-btn-wrapper {
    width: 100%;
  }

  &__close {
    position: absolute;
    top: 11px;
    right: 11px;
    display: flex;
    width: 28px;
    height: 28px;
    align-items: center;
    justify-content: center;
    padding: 4px;
    border: none;
    background: transparent;
    cursor: pointer;
    opacity: 0.6;

    img {
      width: 20px;
      height: 20px;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
    margin-bottom: 32px;
    text-align: center;

    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 700;
      line-height: 32px;
      color: #000;
    }

    p {
      margin: 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
      color: rgba(0, 0, 0, 0.6);
    }
  }

  &__logo {
    width: 153px;
    height: 60px;
    object-fit: contain;
    margin-bottom: 10px;
  }

  &__sent-icon {
    width: 72px;
    height: 72px;
    object-fit: contain;
    margin-bottom: 10px;
  }

  &__google {
    display: flex;
    width: 100%;
    height: 48px;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 8px 24px;
    border: none;
    border-radius: 14px;
    background: #000;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    cursor: pointer;

    img {
      width: 24px;
      height: 24px;
    }

    &:hover {
      opacity: 0.86;
    }
  }

  &__socials {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 48px;
    margin: 24px 0 0;

    button {
      display: flex;
      width: 48px;
      height: 48px;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border: none;
      border-radius: 999px;
      background: rgba(0, 0, 0, 0.08);
      cursor: pointer;
    }

    img {
      display: block;
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  }

  &__divider {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 0 24px;
    margin: 24px 0 0;
    box-sizing: border-box;

    span {
      flex: 1;
      height: 1px;
      border-radius: 10px;
    }

    .divider-first {
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(0, 0, 0, 0.32) 100%
      );
    }

    .divider-last {
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.32) 0%,
        rgba(255, 255, 255, 0) 100%
      );
    }

    em {
      font-style: normal;
      font-size: 14px;
      line-height: 20px;
      color: rgba(0, 0, 0, 0.4);
    }
  }

  &__tabs {
    display: flex;
    height: 40px;
    padding: 2px;
    margin-bottom: 24px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.08);

    button {
      flex: 1;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: rgba(0, 0, 0, 0.7);
      font-size: 15px;
      font-weight: 700;
      line-height: 22px;
      cursor: pointer;

      &.active {
        background: #000;
        color: #fff;
      }
    }
  }

  &__form {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &.compact {
      gap: 12px;
    }
  }

  &__field {
    position: relative;
    display: flex;
    width: 100%;
    height: 48px;
    align-items: center;
    border: 1px solid #ced2e0;
    border-radius: 12px;
    box-sizing: border-box;

    &.error {
      border-color: #ff4d63;
    }

    input {
      width: 100%;
      min-width: 0;
      height: 100%;
      padding: 12px 16px;
      border: none;
      border-radius: inherit;
      background: transparent;
      box-sizing: border-box;
      color: #000;
      font-size: 16px;
      font-weight: 400;
      line-height: 24px;

      &:is(:autofill, :-webkit-autofill) {
        -webkit-box-shadow: inset 0 0 0 1000px #fff !important;
        box-shadow: inset 0 0 0 1000px #fff !important;
        -webkit-text-fill-color: #000 !important;
        caret-color: #000;
      }

      &::placeholder {
        color: #a8acbf !important;
      }
    }

    &.password input {
      padding-right: 48px;
    }
  }

  &__eye,
  &__code {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
    border: none;
    background: transparent;
    cursor: pointer;
  }

  &__eye {
    width: 48px;
    background-image: url("@/assets/icons/circle.svg");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 20px 20px;

    &.visible {
      background-image: url("@/assets/icons/ircle.svg");
    }
  }

  &__code {
    padding: 0 16px;
    color: #2abfc9;
    font-size: 16px;

    &:disabled {
      color: rgba(0, 0, 0, 0.35);
      cursor: not-allowed;
    }
  }

  &__error {
    margin-top: -6px;
    text-align: right;
    color: #ff4d63;
    font-size: 14px;
    line-height: 20px;
  }

  &__actions {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 20px;
  }

  .view-signin &__actions {
    margin-top: 0;
  }

  &__form-meta {
    display: flex;
    min-height: 20px;
    align-items: flex-start;
    gap: 12px;
    margin-top: -4px;
  }

  &__forgot {
    flex: none;
    margin-left: auto;
    padding: 0;
    border: 0;
    color: rgba(0, 0, 0, 0.4);
    background: transparent;
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;
  }

  &__form-error {
    min-width: 0;
    flex: 1;
    margin: 0;
    color: #ff6070;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
    text-align: left;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  &__inline-error,
  &__signup-error {
    margin: 0;
    color: #ff6070;
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
  }

  &__inline-error {
    text-align: left;
    overflow-wrap: anywhere;
  }

  &__forgot-field-group {
    display: flex;
    min-height: 72px;
    flex-direction: column;
    gap: 4px;

    .email-login-modal__field {
      flex-shrink: 0;

      &.error {
        border-color: #ff6070;
      }
    }
  }

  .view-forgot &__form {
    gap: 8px;
  }

  &__signup-error {
    margin-top: -8px;
  }

  &__rules {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin: -4px 0 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      min-height: 28px;
      align-items: center;
      gap: 6px;
      color: rgba(0, 0, 0, 0.4);
      font-size: 14px;
      line-height: 20px;

      img {
        display: block;
        width: 16px;
        height: 16px;
        flex: none;
      }

      &.valid {
        color: rgba(0, 0, 0, 0.6);
      }
    }
  }

  .signup-actions {
    margin-top: -4px;
  }

  .forgot-actions {
    margin-top: 0;
  }

  &__switch-copy {
    margin: 0;
    color: rgba(0, 0, 0, 0.4);
    font-family: "Plus Jakarta Sans", "Inter", Arial, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
    text-align: center;
    white-space: nowrap;
    word-break: break-word;

    button {
      padding: 0;
      border: 0;
      color: inherit;
      background: transparent;
      font: inherit;
      letter-spacing: inherit;
      text-decoration: underline;
      vertical-align: baseline;
      cursor: pointer;
    }
  }

  &__primary {
    display: flex;
    width: 100%;
    height: 48px;
    align-items: center;
    justify-content: center;
    padding: 8px 24px;
    border: none;
    border-radius: 14px;
    background: #48e1eb;
    color: #000;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      background: #37d0dc;
    }
  }

  &__text-button {
    display: block;
    margin: 0 auto;
    padding: 0;
    border: none;
    background: transparent;
    color: rgba(0, 0, 0, 0.4);
    font-size: 14px;
    line-height: 20px;
    cursor: pointer;

    &:hover {
      color: rgba(0, 0, 0, 0.68);
    }
  }

  &__sent {
    display: flex;
    flex-direction: column;
    gap: 16px;
    text-align: center;

    p {
      margin: 0;
      color: rgba(0, 0, 0, 0.6);
      font-size: 14px;
      line-height: 20px;
    }

    .email-login-modal__primary {
      margin-top: 4px;
    }
  }

  &__terms {
    margin-top: 32px;
    color: rgba(0, 0, 0, 0.6);
    font-family: "Plus Jakarta Sans", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15px;
    text-align: center;

    a {
      color: #2abfc9;
      text-decoration: underline;
      cursor: pointer;
    }

    &.cn {
      display: flex;
      align-items: center;
      justify-content: center;
      color: rgba(0, 0, 0, 0.4);
      white-space: normal;

      :deep(.mu-checkbox-group) {
        width: fit-content;
        max-width: 100%;
      }

      :deep(.mu-checkbox) {
        max-width: 100%;
        align-items: flex-start;
        min-height: 24px;
        gap: 8px;
        padding: 0;
        color: rgba(0, 0, 0, 0.4);
        font-family: "Plus Jakarta Sans", sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.15px;
      }

      :deep(.mu-checkbox__control) {
        width: 20px;
        height: 20px;
        flex: 0 0 20px;
        border: 0;
        border-radius: 0;
        background: transparent url("@/assets/icons/terms-unchecked.svg")
          center / 20px 20px no-repeat;
      }

      :deep(.mu-checkbox.is-checked .mu-checkbox__control) {
        background: transparent url("@/assets/icons/terms-checked.svg") center /
          20px 20px no-repeat;
      }

      :deep(.mu-checkbox__check),
      :deep(.mu-checkbox__dash) {
        display: none;
      }

      :deep(.mu-checkbox__label) {
        min-width: 0;
        text-align: left;
        white-space: normal;
      }

      .email-login-modal__terms-text {
        flex: 0 1 auto;
        color: rgba(0, 0, 0, 0.4);
        font-family: "Plus Jakarta Sans", sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 20px;
        letter-spacing: 0.15px;
        text-align: left;
        white-space: normal;
        overflow-wrap: anywhere;

        .a-link {
          color: inherit;
          text-decoration: none;
        }
      }
    }
  }

  @media screen and (max-width: 768px) {
    &__mask {
      padding: 32px;
    }

    &__card {
      max-width: calc(100vw - 64px);
      padding: 24px;
      border-radius: 20px;
    }

    &__header {
      margin-bottom: 24px;
    }

    &__socials {
      gap: 48px;
      margin: 16px 0 24px;
    }

    &__divider {
      margin-bottom: 24px;
    }

    &__actions {
      margin-top: 20px;
    }

    &__terms {
      margin-top: 28px;
      white-space: normal;
    }
  }

  @media screen and (max-width: 430px) {
    &__mask {
      padding: 24px 16px;
    }

    &__card {
      max-width: calc(100vw - 32px);
    }

    &__socials {
      gap: 46px;
    }
  }
}
</style>
